import React, { ReactNode, useEffect } from "react";

export interface UserConfig {
  deviceConnect: string;
  skipAutoRedirect: boolean;
  showTopPlatformLogo: boolean;
  showFooterLenderLogo: boolean;
  footerContent: string;
  otherComponent: { showHelpLineNumber: { isActive: boolean; data: string } };
  theme: {
    themeID: string;
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
    textPrimary: string;
    textSecondary: string;
    textLight: string;
    textWarning: string;
    bg: string;
    bgError: string;
    textError: string;
    buttonBgPrimary: string;
    buttonBgSecondary: string;
    buttonTextPrimary: string;
    buttonTextSecondary: string;
    placeholderText: string;
    progressBar: string;
    cardBorder: string;
    inputBg: string;
    bgLight: string;
    bgCard: string;
    bgSuccess: string;
    fgSuccess: string;
    dashboardGradientPrimary: string;
    dashboardGradientSecondary: string;
    buttonRadius: string;
    cardRadius: string;
    fontFamily: string;
    fontLink: string;
    isDarkTheme: boolean;
    hidePoweredByFinbox: boolean;
    heightButtonSecondary: string;
    heightButtonSmall: string;
    heightButtonPrimary: string;
    minHeightButtonSecondary: string;
    minHeightButtonSmall: string;
    primaryButtonFontSize: string;
    primaryButtonTextTransform: string;
  };
}

const defaultTheme = (isDark: boolean) => {
  return {
    font: {
      link: "",
      name: "Gilroy",
    },
    primaryButton: {
      fontSize: "16px",
      textTransform: "none",
    },
    height: {
      buttonPrimary: "54px",
      buttonSecondary: "54px",
      buttonSmall: "2rem",
    },
    minHeight: {
      buttonSecondary: "40px",
      buttonSmall: "2rem",
    },
    colors: {
      bg: isDark ? "black" : "white",

      primaryColor: "#0075FF",
      secondaryColor: "#0075FF",
      tertiaryColor: "#D3D3D3",

      bgError: "#fdf0ef",

      buttonBgPrimary: "#0075FF",
      buttonBgSecondary: "#fff",
      buttonTextPrimary: "#fff",
      buttonTextSecondary: "#0075FF",

      inputBg: "#fafafa",

      textPrimary: "#2D2D2D",
      textSecondary: "#6A6A6A",
      textLight: "#C2CEFF",
      textWarning: "#e9b140",
      textError: "#e94f40",

      placeholderText: "#dbdbdb",
      progressBar: "#24CA7A",

      bgLight: "#FAFAFA",

      bgCard: "#FAFAFA",
      cardBorder: "#D5DDE3",

      bgSuccess: "#24CA7A",
      fgSuccess: "white",

      dashboardGradientPrimary: "#19348C",
      dashboardGradientSecondary: "#4D7AED",
    },
    radius: {
      button: "2px",
      card: "4px",
    },
    isDark: false,
  };
};

const applyFontSettings = (fontLink: string, fontName: string) => {
  if (!fontName || !fontLink) return;
  const link = document.createElement("link");
  link.setAttribute("href", fontLink);
  link.setAttribute("rel", "stylesheet");
  document.head.appendChild(link);
  document.body.style.fontFamily = fontName;
};

type SaveThemeType = (newTheme: UserConfig["theme"]) => void;

const ThemeContext = React.createContext<SaveThemeType | null>(null);

export const FinBoxThemeProvider = ({
  isDark,
  children,
}: {
  isDark: boolean;
  children: ReactNode;
}) => {
  const documentStyle = document.documentElement.style;
  const tempTheme = defaultTheme(isDark);
  // https://lendinguat11.finbox.in/v1/user/config

  const saveTheme: SaveThemeType = (newTheme: UserConfig["theme"]) => {
    if (!newTheme) return;
    const { colors, radius, height, minHeight, primaryButton } = tempTheme;
    const styleProperties: [string, string][] = [
      [
        "--color-buttonBgPrimary",
        newTheme.buttonBgPrimary || colors.buttonBgPrimary,
      ],
      [
        "--color-buttonBgSecondary",
        newTheme.buttonBgSecondary || colors.buttonBgSecondary,
      ],
      [
        "--color-buttonTextPrimary",
        newTheme.buttonTextPrimary || colors.buttonTextPrimary,
      ],
      [
        "--color-buttonTextSecondary",
        newTheme.buttonTextSecondary || colors.buttonTextSecondary,
      ],
      ["--color-primaryColor", newTheme.primaryColor || colors.primaryColor],
      ["--color-textPrimary", newTheme.textPrimary || colors.textPrimary],
      ["--color-textError", newTheme.textError || colors.textError],
      ["--radius-button", newTheme.buttonRadius || radius.button],
      ["--color-bg", newTheme.bg || colors.bg],
      ["--color-bgCard", newTheme.bgCard || colors.bgCard],
      ["--color-bgError", newTheme.bgError || colors.bgError],
      ["--color-bgLight", newTheme.bgLight || colors.bgLight],
      ["--color-bgSuccess", newTheme.bgSuccess || colors.bgSuccess],
      ["--color-cardBorder", newTheme.cardBorder || colors.cardBorder],
      ["--color-inputBorder", newTheme.inputBg || colors.inputBg],
      [
        "--color-dashboardGradientPrimary",
        newTheme.dashboardGradientPrimary || colors.dashboardGradientPrimary,
      ],
      [
        "--color-dashboardGradientSecondary",
        newTheme.dashboardGradientSecondary ||
          colors.dashboardGradientSecondary,
      ],
      ["--color-fgSuccess", newTheme.fgSuccess || colors.fgSuccess],
      ["--color-inputBg", newTheme.inputBg || colors.inputBg],
      [
        "--color-placeholderText",
        newTheme.placeholderText || colors.placeholderText,
      ],
      ["--color-progressBar", newTheme.progressBar || colors.progressBar],
      [
        "--color-secondaryColor",
        newTheme.secondaryColor || colors.secondaryColor,
      ],
      ["--color-tertiaryColor", newTheme.tertiaryColor || colors.tertiaryColor],
      ["--color-textLight", newTheme.textLight || colors.textLight],
      ["--color-textSecondary", newTheme.textSecondary || colors.textSecondary],
      ["--color-textWarning", newTheme.textWarning || colors.textWarning],
      ["--radius-card", newTheme.cardRadius || radius.card],
      [
        "--height-buttonSecondary",
        newTheme.heightButtonSecondary || height?.buttonSecondary,
      ],
      [
        "--height-buttonPrimary",
        newTheme.heightButtonPrimary || height?.buttonPrimary,
      ],
      [
        "--height-buttonSmall",
        newTheme.heightButtonSmall || height?.buttonSmall,
      ],
      [
        "--min-height-buttonSecondary",
        newTheme.minHeightButtonSecondary || minHeight?.buttonSecondary,
      ],
      [
        "--primaryButton-buttonSmall",
        newTheme.primaryButtonFontSize || primaryButton?.fontSize,
      ],
      [
        "--primaryButton-buttonSmall",
        newTheme.primaryButtonTextTransform || primaryButton?.textTransform,
      ],
    ];

    styleProperties.forEach(([property, value]) => {
      documentStyle.setProperty(property, value);
    });

    applyFontSettings(newTheme.fontLink, newTheme.fontFamily);
  };

  useEffect(() => {
    saveTheme({} as UserConfig["theme"]);
  }, []);

  return (
    <ThemeContext.Provider value={saveTheme}>{children}</ThemeContext.Provider>
  );
};

export default FinBoxThemeProvider;
